import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  OverlayTrigger,
  Popover,
  Stack,
  Badge,
  Row,
  Col,
  ListGroup,
  Form,
  InputGroup,
} from "react-bootstrap";
import { Pencil, Plus, Search } from "react-bootstrap-icons";
import { Contact } from "@/entities/contact";
import { useMessageSelectionStore, useScrollStore } from "@/features/chat/model";
import { useMediaQuery } from "react-responsive";
import { MessageSearchInput, MessageSearchInputHandle } from "./message-search-input-handle";
import { SelectedMessagesActions } from "./selected-messages-action";
import { useShallow } from "zustand/react/shallow";
import { formatPhone } from "@/shared/ui";
import { enqueueSnackbar } from "notistack";
import { setAdmin } from "@/entities/user/api";

type HeaderProps = {
  name: string;
  contact?: Contact;
  chatId?: string;
  userId?: string;
  isAdmin: boolean;
  onClick: () => void;
  onDeleteMessages: (ids: string[]) => void;
  onForwardMessages: (ids: string[]) => void;
  onUpdateContact: (chatId: string, firstName: string, lastName: string) => void;
};

export const ChatMessagesHeader = ({
  name,
  contact,
  userId,
  chatId,
  isAdmin,
  onClick,
  onDeleteMessages,
  onForwardMessages,
  onUpdateContact,
}: HeaderProps) => {
  const modeSelected = useMessageSelectionStore(useShallow((state) => state.modeSelected));
  const [showRenameInput, setShowRenameInput] = useState(false);
  const [contactName, setContactName] = useState<
    Record<string, { firstName: string; lastName: string }>
  >({});

  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const [isSearchActive, setIsSearchActive] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchIconRef = useRef<HTMLButtonElement>(null);

  const handleSetAdmin = async () => {
    if (!contact?.user._id) return;

    try {
      const res = await setAdmin(contact.user._id);
      if (res.success) {
        enqueueSnackbar("Пользователь назначен администратором", { variant: "success" });
      } else {
        enqueueSnackbar("Не удалось назначить администратора", { variant: "error" });
      }
    } catch (err) {
      enqueueSnackbar("Ошибка при назначении администратора", { variant: "error" });
    }
  };


  const triggerScrollToMessage = useCallback(
    (chatId: string, messageId: string) => {
      const scrollToFn = useScrollStore.getState().getScrollToMessage(chatId);
      scrollToFn?.(chatId, messageId);
    },
    [chatId]
  );

  const handleSaveContact = () => {
    if (chatId && userId && contactName[chatId]) {
      const { firstName, lastName } = contactName[chatId];
      onUpdateContact(chatId, firstName, lastName);
      setShowRenameInput(false);
    }
  };

  useEffect(() => {
    return () => {
      setShowRenameInput(false);
    };
  }, [chatId]);

  useEffect(() => {
    if (chatId && contact && !contactName[chatId]) {
      setContactName((prev) => ({
        ...prev,
        [chatId]: {
          firstName: contact.firstName,
          lastName: contact.lastName,
        },
      }));
    }
  }, [chatId, contact]);

  const searchInputRef = useRef<MessageSearchInputHandle>(null);

  if (modeSelected) {
    return <SelectedMessagesActions
      chatId={chatId}
      onDeleteMessages={onDeleteMessages}
      onForwardMessages={onForwardMessages}
    />;
  }

  const popover = (
    <Popover id="chat-popover" style={{ zIndex: 1301 }}>
      <Popover.Body>
        {contact ? (
          <Stack gap={3}>
            <Row className="align-items-center">
              <Col xs="auto">
                <div
                  className="rounded-circle bg-secondary text-white d-flex justify-content-center align-items-center"
                  style={{ width: 54, height: 54, fontSize: 21 }}
                >
                  {name[0]?.toUpperCase()}
                </div>
              </Col>
              <Col>
                <div className="d-flex align-items-center">
                  {showRenameInput ? (
                    <Row className="gap-1">
                      <Form.Control
                        type="text"
                        id="firstName"
                        placeholder="Имя"
                        value={
                          chatId ? contactName[chatId]?.firstName || "" : ""
                        }
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          chatId &&
                          setContactName((prev) => ({
                            ...prev,
                            [chatId]: {
                              ...prev[chatId],
                              firstName: e.target.value,
                            },
                          }))
                        }
                        aria-describedby="firstNameHelpBlock"
                        className="rounded-5"
                      />

                      <Form.Control
                        type="text"
                        id="lastName"
                        placeholder="Фамилия"
                        value={
                          chatId ? contactName[chatId]?.lastName || "" : ""
                        }
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          chatId &&
                          setContactName((prev) => ({
                            ...prev,
                            [chatId]: {
                              ...prev[chatId],
                              lastName: e.target.value,
                            },
                          }))
                        }
                        aria-describedby="lastNameHelpBlock"
                        className="rounded-5"
                      />
                      <Button
                        onClick={handleSaveContact}
                        size="sm"
                        variant="outline-secondary"
                      >
                        Сохранить
                      </Button>
                    </Row>
                  ) : (
                    <>
                      <h5 style={{ marginBottom: 0 }}>{name}</h5>
                      <Button
                        variant="link"
                        onClick={() => setShowRenameInput(true)}
                      >
                        <Pencil style={{ color: "#111" }} />
                      </Button>
                    </>
                  )}
                </div>
              </Col>
            </Row>

            <ListGroup variant="flush">
              <ListGroup.Item className="d-flex justify-content-between align-items-center px-0">
                <span className="fw-bold">Имя</span>
                <span>{contact.user.firstName}</span>
              </ListGroup.Item>
              <ListGroup.Item className="d-flex justify-content-between align-items-center px-0">
                <span className="fw-bold">Фамилия</span>
                <span>{contact.user.lastName}</span>
              </ListGroup.Item>
              <ListGroup.Item className="d-flex justify-content-between align-items-center px-0">
                <span className="fw-bold">Телефон:</span>
                <span>{formatPhone(contact.user.phone)}</span>
              </ListGroup.Item>

              <ListGroup.Item className="d-flex gap-2 justify-content-between align-items-center px-0">
                <span className="fw-bold">Создан:</span>
                <span>
                  {new Date(contact.user.createdAt).toLocaleDateString(
                    "ru-RU",
                    {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    }
                  )}{" "}
                  {new Date(contact.user.createdAt).toLocaleTimeString(
                    "ru-RU",
                    {
                      hour: "2-digit",
                      minute: "2-digit",
                    }
                  )}
                </span>
              </ListGroup.Item>

              <ListGroup.Item className="d-flex justify-content-between align-items-center px-0">
                <span className="fw-bold">Статус:</span>
                <Badge
                  bg={
                    contact.user.status === "REGISTERED" ? "success" : "warning"
                  }
                >
                  {contact.user.status === "REGISTERED"
                    ? "Зарегистрирован"
                    : "Приглашён"}
                </Badge>
              </ListGroup.Item>

              {isAdmin && contact && (
                <ListGroup.Item className="d-flex justify-content-end align-items-center px-0">
                  <div className="mt-3 text-end">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleSetAdmin}
                    >
                      Сделать админом
                    </Button>
                  </div>
                </ListGroup.Item>
              )}
            </ListGroup>
          </Stack>
        ) : (
          <p className="mb-0">Нет данных чата</p>
        )}
      </Popover.Body>
    </Popover>
  );

  const handleSearchIconClick = () => {
    if (isMobile) {
      setIsSearchActive(true);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  };

  const handleClearSearch = () => {
    setIsSearchActive(false);
  };

  return (
    <Stack
      direction="horizontal"
      gap={2}
      className={`justify-content-${!contact ? "end" : "between"
        } mb-2 align-items-center`}
    >
      {contact &&
        chatId && <>
          {(!isMobile || (isMobile && !isSearchActive)) && (
            <OverlayTrigger
              trigger="click"
              placement="bottom"
              overlay={popover}
              rootClose
            >
              <Stack
                direction="horizontal"
                gap={2}
                style={{
                  cursor: "pointer",
                  position: "relative",
                  maxWidth: isMobile ? "calc(100% - 152px)" : "calc(100% - 402px)",
                }}
                className={`align-items-center ${isAdmin ? "ms-5" : "ms-0"
                  } ms-lg-0`}
              >
                <div
                  className="rounded-circle bg-secondary text-white d-flex justify-content-center align-items-center"
                  style={{
                    position: "relative",
                    width: 38,
                    minWidth: 38,
                    height: 38,
                    fontSize: 21,
                  }}
                >
                  {name[0]?.toUpperCase()}
                  {contact?.user.isOnline && (
                    <div
                      className="position-absolute rounded-circle border border-white"
                      style={{
                        width: 10,
                        height: 10,
                        bottom: 0,
                        right: 0,
                        backgroundColor: "#12ea12",
                      }}
                    />
                  )}
                </div>
                <h5 className="mb-0 text-truncate flex-grow-1" style={{ lineHeight: 1.7 }} title={name}>
                  {name}
                </h5>
              </Stack>
            </OverlayTrigger>
          )}

          <div
            ref={wrapperRef}
            style={{ minWidth: isMobile ? 10 : 250 }}
            className="position-relative d-flex justify-content-end flex-grow-1"
          >
            {isMobile && !isSearchActive ? (
              <Button
                variant="outline-secondary"
                ref={searchIconRef}
                onClick={handleSearchIconClick}
                style={{ cursor: 'pointer', padding: '0', width: 38, height: 38 }}
              >
                <Search size={18} style={{ marginTop: -2 }} />
              </Button>
            ) : (
              <MessageSearchInput
                ref={searchInputRef}
                chatId={chatId}
                onSelectMessage={(id, messageId) => triggerScrollToMessage(id || chatId || '', messageId)}
                onClear={handleClearSearch}
              />
            )}
          </div>
        </>
      }

      {isAdmin &&
        (!isMobile || (isMobile && !isSearchActive)) && (
          <Button
            variant="outline-secondary"
            className="text-nowrap d-flex align-items-center justify-content-center"
            onClick={onClick}
            style={{ width: isMobile ? 38 : 'auto', padding: isMobile ? 0 : "4px 14px", height: 38 }}
          >
            {isMobile ? <Plus size={20} /> : 'Новый чат'}
          </Button>
        )}
    </Stack>
  );
};
